import { useEffect, useState } from 'react'
import { ExchangeTradingSession } from '../proto/generated/trading-calendar'
import { getFutureTradingSessionBoundaries, isWithinChartLiveTradingHours } from '../util/tradingSessionLive'
import { TradingSessionSecurityRef } from '../util/tradingSessionSelection'

/**
 * Whether the security is in a streaming session window now, recomputed on session boundaries
 * (aligned with market status / trading calendar data). Selection: exchange → segment → scope.
 */
export function useExchangeLiveTradingWindow(
    exchangeTradingSessions: ExchangeTradingSession[] | undefined,
    security: TradingSessionSecurityRef | undefined
): boolean {
    const [live, setLive] = useState(false)
    const [boundaryTick, setBoundaryTick] = useState(0)

    useEffect(() => {
        if (!exchangeTradingSessions?.length || security?.exchange === undefined) {
            setLive(false)
            return
        }

        const recompute = () => {
            setLive(isWithinChartLiveTradingHours(exchangeTradingSessions, security))
        }

        recompute()

        const now = Date.now()
        const timeouts: ReturnType<typeof setTimeout>[] = []
        for (const boundary of getFutureTradingSessionBoundaries(exchangeTradingSessions, security, now)) {
            const delay = boundary - now
            if (delay > 0) {
                timeouts.push(
                    setTimeout(() => {
                        recompute()
                        setBoundaryTick((t) => t + 1)
                    }, delay)
                )
            }
        }

        const interval = setInterval(recompute, 60_000)

        return () => {
            timeouts.forEach(clearTimeout)
            clearInterval(interval)
        }
    }, [
        exchangeTradingSessions,
        security?.exchange,
        security?.segment,
        security?.partOfCAS,
        boundaryTick
    ])

    return live
}
